import type { Point2 } from '../geometry/point';

/**
 * View rotation, in clockwise quarter turns of the *content*.
 *
 * A free angle is deliberately not offered: quarter turns keep fitting exact (a
 * quarter-turned AABB is just swapped extents) and keep the rotation matrix's
 * entries to 0 and ±1, so repeated rotation accumulates no floating-point drift.
 */
export type QuarterTurns = 0 | 1 | 2 | 3;

/**
 * Viewport state for a 2D drawing viewer, as a similarity transform between the
 * drawing's world space and the container's screen space.
 *
 * Drawing world space is y-up; screen space is y-down, so the mapping flips y.
 */
export type ViewportState = {
  /** World-space point displayed at the centre of the container. */
  readonly center: Point2;
  /** Screen pixels per world unit. Always > 0. */
  readonly scale: number;
  /** Clockwise quarter turns applied to the content about the container centre. */
  readonly quarterTurns: QuarterTurns;
};

/** Container dimensions in CSS pixels. */
export type ViewportSize = {
  readonly width: number;
  readonly height: number;
};

export const DEFAULT_VIEWPORT: ViewportState = {
  center: [0, 0],
  scale: 1,
  quarterTurns: 0,
};

export const EMPTY_VIEWPORT_SIZE: ViewportSize = { width: 0, height: 0 };

/** Normalises any integer quarter-turn count into the `0..3` range. */
export const normalizeQuarterTurns = (turns: number): QuarterTurns =>
  (((turns % 4) + 4) % 4) as QuarterTurns;

/** True when the view is turned onto its side, so screen extents are swapped. */
export const isQuarterTurned = (viewport: ViewportState): boolean =>
  viewport.quarterTurns % 2 === 1;
